import { truncateMessage } from '@utils/locale.utils';
import { getSectionCenterCoordinates } from '@utils/map/automatonSection.utils';
import {
  getIconBasicInfoFromSensor,
  getIconBasicInfoFromSubSensorInfo,
  getSensorSpriteIconName,
  getSubSensorSpriteIconName,
} from '@utils/map/sensor.utils';
import {
  getSensorUniqueKey,
  getSubSensorConfigsFromParent,
  getSubsensorInfos,
  hasSpecificState,
} from '@utils/sensors/sensors.utils';
import { toLonLatArray } from '@utils/validation/coordinates.utils';
import { Feature } from 'geojson';

import { Coordinates } from '@/types/commons/commons.types';
import { MapFilters } from '@/types/filters.types';
import {
  FeatureTypeEnum,
  SensorLikeDisplayedOnMap,
  SensorUniqueKeys,
  SplitMonitoringSensors,
  SubSensorInfo,
} from '@/types/map.types';
import { SensorWithSubSensorType } from '@/types/sensor/configuration.types';
import { MonitoringSensorByType } from '@/types/sensor/monitoring.types';
import {
  AutomatonCabinet,
  AutomatonSection,
  AutomatonSensorConfiguration,
  EffectiveSensorStatusEnum,
  SpecificStateEnum,
} from '@/types/sensor/sensors';

export const TRUNCATE_SIZE_NAME = 10;

export function toSensorIconFeature(
  sensorUniqueKeys: SensorUniqueKeys,
  position: Coordinates,
  type: SensorLikeDisplayedOnMap,
  active: boolean,
  maintenance: boolean,
  sensorSpriteIconName: string,
  name: string,
  status?: EffectiveSensorStatusEnum,
  specificState?: SpecificStateEnum | null,
) {
  const [icSymb, sensorTrigram, ...rest] = sensorSpriteIconName.split('-');
  return {
    type: 'Feature',
    id: `${FeatureTypeEnum.SENSOR_ICON}-${sensorUniqueKeys.sensorUniqueKey}`,
    properties: {
      featureType: FeatureTypeEnum.SENSOR_ICON,
      id: sensorUniqueKeys.sensorUniqueKey,
      value: sensorUniqueKeys,
      type,
      baseIcon: [icSymb, sensorTrigram, rest.at(-1)].join('-'), //Remove states
      iconS: [icSymb, ...rest, 'S'].join('-'), //Remove sensorTrigram and add small label
      iconL: `${sensorSpriteIconName}-L`, //Add large label
      status: status ?? null,
      specificState,
      maintenance,
      active,
      selectOptionLabel: name,
    },
    geometry: {
      type: 'Point',
      coordinates: toLonLatArray(position, false)!,
    },
  } as Feature;
}

export function toSensorLabelFeature(
  sensorUniqueKeys: SensorUniqueKeys,
  position: Coordinates,
  name: string,
  type: SensorLikeDisplayedOnMap,
): Feature {
  return {
    type: 'Feature',
    id: `${FeatureTypeEnum.SENSOR_LABEL}-${sensorUniqueKeys.sensorUniqueKey}`,
    properties: {
      id: sensorUniqueKeys.sensorUniqueKey,
      featureType: FeatureTypeEnum.SENSOR_LABEL,
      value: sensorUniqueKeys,
      type,
      textField: truncateMessage(name, TRUNCATE_SIZE_NAME),
      tooltipAnchor: 'pointer',
      showTooltip: name.length > TRUNCATE_SIZE_NAME,
      selectOptionLabel: name,
    },
    geometry: {
      type: 'Point',
      coordinates: toLonLatArray(position, false)!,
    },
  };
}

export function toSubSensorIconFeature(subSensorInfo: SubSensorInfo<SensorWithSubSensorType>) {
  const { parentSensorConfig, subSensorConfig, subSensorStatus } = subSensorInfo;
  const subSensorSpriteIconName = getSubSensorSpriteIconName(subSensorInfo);
  const { active, maintenance, status } = getIconBasicInfoFromSubSensorInfo(subSensorInfo);
  const specificState =
    subSensorStatus && hasSpecificState(subSensorStatus) ? subSensorStatus.specificState : undefined;
  return toSensorIconFeature(
    {
      sensorUniqueKey: getSensorUniqueKey(subSensorConfig),
      parentUniqueKey: getSensorUniqueKey(parentSensorConfig),
    },
    subSensorConfig.position,
    subSensorConfig.type,
    active,
    maintenance,
    subSensorSpriteIconName,
    subSensorConfig.name,
    status,
    specificState,
  );
}

const toSubSensorIconFeatures = (
  parentMonitoringSensors: MonitoringSensorByType<SensorWithSubSensorType>[],
): Feature[] => {
  return parentMonitoringSensors.flatMap(getSubsensorInfos).map(toSubSensorIconFeature);
};

const toSubSensorLabelFeatures = (
  parentMonitoringSensors: MonitoringSensorByType<SensorWithSubSensorType>[],
): Feature[] => {
  return parentMonitoringSensors.flatMap((parentMonitoringSensor) =>
    getSubSensorConfigsFromParent(parentMonitoringSensor.configuration).flatMap((subSensor) => {
      return toSensorLabelFeature(
        {
          sensorUniqueKey: getSensorUniqueKey(subSensor),
          parentUniqueKey: getSensorUniqueKey(parentMonitoringSensor),
        },
        subSensor.position,
        subSensor.name,
        subSensor.type,
      );
    }),
  );
};

export const toMonitoringSensorIconFeatures = ({
  regularSensors,
  withSubSensors,
}: SplitMonitoringSensors): Feature[] => {
  const regularSensorFeatures = regularSensors.map((monitoringSensor) => {
    const active = monitoringSensor.active;
    const maintenance = monitoringSensor.configuration.maintenance;
    const sensorPosition = monitoringSensor.configuration.sensorPosition;
    const type = monitoringSensor.configuration.type;
    const sensorStatus = monitoringSensor.status?.effectiveStatus;
    const specificState =
      monitoringSensor.status && hasSpecificState(monitoringSensor.status)
        ? monitoringSensor.status.specificState
        : undefined;
    return toSensorIconFeature(
      {
        sensorUniqueKey: getSensorUniqueKey(monitoringSensor),
      },
      sensorPosition,
      type,
      active,
      maintenance,
      getSensorSpriteIconName(getIconBasicInfoFromSensor(monitoringSensor)),
      monitoringSensor.configuration.name,
      sensorStatus || undefined,
      specificState,
    );
  });
  return [...regularSensorFeatures, ...toSubSensorIconFeatures(withSubSensors)];
};

export const toMonitoringSensorLabelFeatures = ({
  regularSensors,
  withSubSensors,
}: SplitMonitoringSensors): Feature[] => {
  const regularSensorFeatures = regularSensors.map((monitoringSensor) =>
    toSensorLabelFeature(
      {
        sensorUniqueKey: getSensorUniqueKey(monitoringSensor),
      },
      monitoringSensor.configuration.sensorPosition,
      monitoringSensor.configuration.name,
      monitoringSensor.configuration.type,
    ),
  );

  return [...regularSensorFeatures, ...toSubSensorLabelFeatures(withSubSensors)];
};

export const toPerimeterLinkFeatures = (
  automaton: AutomatonSensorConfiguration,
  cabinet: AutomatonCabinet,
  sections: AutomatonSection[],
  mapFilters: MapFilters,
  zoom?: number,
): Feature[] => {
  const automatonPosition = toLonLatArray(automaton.sensorPosition, false)!;
  const cabinetPosition = toLonLatArray(cabinet.position, false)!;
  const featureLinks: Feature[] = [];
  if (mapFilters.displayPerimeterSensorCabinet) {
    if (mapFilters.displayPerimeterSensorAutomaton) {
      featureLinks.push({
        type: 'Feature',
        id: `${FeatureTypeEnum.PERIMETER_LINK}-${automaton.id}-${cabinet.id}`,
        properties: {
          featureType: FeatureTypeEnum.PERIMETER_LINK,
        },
        geometry: {
          type: 'LineString',
          coordinates: [automatonPosition, cabinetPosition],
        },
      });
    }
    if (mapFilters.displayPerimeterSensorSegment) {
      for (const section of sections) {
        const sectionCenter = toLonLatArray(getSectionCenterCoordinates(section, zoom), false)!;
        featureLinks.push({
          type: 'Feature',
          id: `${FeatureTypeEnum.PERIMETER_LINK}-${automaton.id}-${cabinet.id}-section`,
          properties: {
            featureType: FeatureTypeEnum.PERIMETER_LINK,
          },
          geometry: {
            type: 'LineString',
            coordinates: [cabinetPosition, sectionCenter],
          },
        } as Feature);
      }
    }
  }

  return featureLinks;
};
